/**
 * Layout lifted off the official দূতাবাস ফরম-১ (জন্ম নিবন্ধন আবেদনপত্র, বিধি ৬,
 * জন্ম ও মৃত্যু নিবন্ধন (দূতাবাস) বিধিমালা, ২০০৬).
 *
 * The file the form ships as is a single 300 dpi scan with no text layer, so
 * everything here was measured off a 4× render: table rules as long dark runs,
 * dotted leaders as evenly spaced dots (2.26 pt pitch), tick boxes as small
 * hollow squares, and each label's left edge and baseline from its ink box and
 * its headline (matra) row.
 *
 * Coordinates are in the scan's own A4 space (595 × 842 pt, y down from the top,
 * text by baseline). The scan sits ~9.7 pt right of centre; `X_SHIFT` moves it
 * back so the margins are level on the printed sheet.
 */

/** Page box — A4 at 72 dpi. */
export const PAGE_WIDTH = 595;
export const PAGE_HEIGHT = 842;

/** The tables run 85.4 → 529.0 on the scan; this centres them on the page. */
export const X_SHIFT = -9.7;

/**
 * Kalpurush sets ~1.36× wider than the form's own face, so sizes are solved
 * from measured ink widths, not copied: the body solves to 7.7 px (median of
 * 49 labels, all within ±4 %), the title to 9.5 px and the rule line to 9.6 px.
 *
 * Everything is set at 400. Kalpurush ships one weight, so 700 is the browser's
 * synthetic bold — which html2canvas does not reproduce, so bold headings in
 * the preview printed regular and the two no longer looked the same.
 */
export const TYPE = {
    body: { size: 7.7, weight: 400 },
    head: { size: 7.7, weight: 400 },
    title: { size: 9.5, weight: 400 },
    sup: { size: 5, weight: 400 },
};

/** Text sits in a 1.5 em line box, so dots and descenders stay inside the clip. */
export const LINE_BOX = 1.5;

/** Kalpurush: ascent 1 em, descent 0.4 em → baseline 1.05 em into a 1.5 em box. */
export const BASELINE_RATIO = (LINE_BOX - 1.4) / 2 + 1;

/** Dot pitch of the printed leaders. */
export const PITCH = 2.26;

/**
 * Leaders are real full stops. Kalpurush's period advances 0.25 em, so each
 * run takes a little tracking to reach the printed pitch.
 */
export const LEADER = {
    char: '.',
    count: 120,
    letterSpacing: (size, pitch) => pitch - size * 0.25,
};

/** Table rules: the scan's strokes read 0.6 pt; the footnote rule is heavier. */
export const RULE = { stroke: 0.6, color: '#222' };

/**
 * Typed answers are not part of the printed form, so they print larger than the
 * labels to stay legible on a photocopy, and drop toward `minSize` only when the
 * cell is too short for the answer.
 */
export const VALUE_TYPE = { size: 9.4, minSize: 6.4, weight: 400 };

/**
 * Kalpurush ink runs from 0.94 em above the baseline (the ি / ী hooks) to 0.23 em
 * below it (ু, ্র), measured off the browser. A cell answer is capped so that span
 * clears both rules, and its baseline set so the span is centred between them.
 */
export const VALUE_INK = { ascent: 0.94, descent: 0.23, clearance: 1.4 };
export const cellFit = ([top, bottom], size) => {
    const fitted = Math.min(size, (bottom - top - VALUE_INK.clearance) / (VALUE_INK.ascent + VALUE_INK.descent));
    const baseline = (top + bottom) / 2 + ((VALUE_INK.ascent - VALUE_INK.descent) / 2) * fitted;
    return { size: fitted, baseline };
};

/** Answers set on a leader sit a little above the dots, the way handwriting does. */
export const VALUE_LIFT = 1.8;
export const VALUE_INSET = 3;

/** The two table edges every block shares. */
const L = 85.4;
const R = 529.0;

const text = (t, y, x, extra = {}) => ({ k: 'text', t, y, x, ...extra });
const center = (t, y, c, extra = {}) => ({ k: 'text', t, y, center: c, ...extra });
const head = (t, y, x) => text(t, y, x, { weight: TYPE.head.weight });
const leader = (y, from, to) => ({ k: 'leader', y, from, to });
const hline = (y, x0 = L, x1 = R, extra = {}) => ({ k: 'hline', y, x0, x1, ...extra });
const vline = (x, y0, y1) => ({ k: 'vline', x, y0, y1 });
/** A printed tick box, ticked when `form[field] === when`. */
const tick = (field, when, x, y, w, h) => ({ k: 'tick', field, when, x, y, w, h });
/** An answer on a leader: `from`/`to` are the dots' span, `y` their baseline. */
const value = (field, y, from, to, extra = {}) => ({ k: 'value', field, y, from, to, lift: 0, ...extra });
/**
 * An answer in a table cell: `row` is the cell's top and bottom rule, and the
 * answer is sized and set so its ink sits midway between them (see `cellBaseline`).
 */
const inCell = (field, row, from, to, extra = {}) => ({ k: 'value', field, row, from, to, ...extra });
const cellValue = (field, row, from, to, extra = {}) => inCell(field, row, from, to, { align: 'center', ...extra });

/** Middle of a cell, for the labels the form centres. */
const mid = (a, b) => (a + b) / 2;

export const PAGE = [
    // Heading
    text('(দূতাবাস ফরম - ১)', 53.3, 474.5),
    center('জন্ম নিবন্ধন আবেদনপত্র', 67.4, 305.9, { size: TYPE.title.size, weight: TYPE.title.weight }),
    // The footnote mark: the form says it may be copied and handed out free.
    text('১', 62.4, 348.6, { size: TYPE.sup.size }),
    center('[বিধি ৬, জন্ম  ও মৃত্যু নিবন্ধন  (দূতাবাস) বিধিমালা, ২০০৬]', 82.3, 306.4, { size: 9.6, weight: TYPE.title.weight }),

    // ১. নিবন্ধনাধীন ব্যক্তির বিবরণ
    head('১. নিবন্ধনাধীন ব্যক্তির বিবরণ:', 95.4, 85.6),
    hline(98.4), hline(113.6), hline(126.4, 158.9, 443.5), hline(139.0, 126.3, 443.5), hline(151.9), hline(163.1),
    vline(L, 98.4, 163.1), vline(R, 98.4, 163.1),
    vline(126.3, 98.4, 151.9), vline(158.9, 113.6, 163.1), vline(236.1, 113.6, 151.9),
    vline(321.4, 113.6, 163.1), vline(443.5, 113.6, 151.9), vline(480.1, 113.6, 151.9), vline(404.9, 151.9, 163.1),

    text('নাম', 106.6, 90.5, { weight: TYPE.head.weight }),
    inCell('registrantName', [98.4, 113.6], 126.3 + VALUE_INSET, R - VALUE_INSET),

    text('জন্ম তারিখ', 129.9, 91.4),
    text('(খ্রী:)', 140.3, 90.4),
    center('দিন', 122.5, mid(158.9, 236.1)),
    center('মাস', 122.5, mid(236.1, 321.4)),
    center('বৎসর', 122.5, mid(321.4, 443.5)),
    text('সংখ্যায়', 134.4, 131.4),
    cellValue('birthDay', [126.4, 139.0], 158.9, 236.1),
    cellValue('birthMonth', [126.4, 139.0], 236.1, 321.4),
    cellValue('birthYear', [126.4, 139.0], 321.4, 443.5),
    text('কথায়', 148.0, 135.3),
    cellValue('birthDayWords', [139.0, 151.9], 158.9, 236.1),
    cellValue('birthMonthWords', [139.0, 151.9], 236.1, 321.4),
    cellValue('birthYearWords', [139.0, 151.9], 321.4, 443.5),

    center('লিঙ্গ', 135.0, mid(443.5, 480.1)),
    tick('gender', 'female', 495.0, 124.8, 4.6, 5.0),
    text('নারী', 130.0, 501.3),
    tick('gender', 'male', 494.7, 136.9, 4.6, 5.0),
    text('পুরুষ', 141.8, 501.6),

    center('জন্মস্থান', 160.1, 122.3),
    inCell('birthPlace', [151.9, 163.1], 158.9 + VALUE_INSET, 321.4 - VALUE_INSET),
    center('পাসপোর্ট নম্বর (যদি থাকে)', 159.7, mid(321.4, 404.9)),
    inCell('passportNo', [151.9, 163.1], 404.9 + VALUE_INSET, R - VALUE_INSET),

    // ২. পিতা ও মাতার বিবরণ
    head('২. পিতা ও মাতার বিবরণ :', 171.1, 85.4),
    hline(179.4), hline(190.5), hline(201.7), hline(212.6),
    vline(L, 179.4, 212.6), vline(208.5, 179.4, 212.6), vline(311.5, 179.4, 212.6),
    vline(411.5, 179.4, 212.6), vline(R, 179.4, 212.6),
    center('পিতার নাম', 187.3, mid(L, 208.5)),
    center('জাতীয়তা', 198.7, mid(L, 208.5)),
    center('পাসপোর্ট নম্বর', 209.5, mid(L, 208.5)),
    center('মাতার নাম', 187.3, mid(311.5, 411.5)),
    center('জাতীয়তা', 198.7, mid(311.5, 411.5)),
    center('পাসপোর্ট নম্বর', 209.5, mid(311.5, 411.5)),
    cellValue('fatherName', [179.4, 190.5], 208.5, 311.5),
    cellValue('fatherNationality', [190.5, 201.7], 208.5, 311.5),
    cellValue('fatherPassport', [201.7, 212.6], 208.5, 311.5),
    cellValue('motherName', [179.4, 190.5], 411.5, R),
    cellValue('motherNationality', [190.5, 201.7], 411.5, R),
    cellValue('motherPassport', [201.7, 212.6], 411.5, R),

    // ৩. স্থায়ী ঠিকানা — two ruled lines the address wraps across
    head('৩। নিবন্ধনাধীন ব্যক্তির বাংলাদেশস্থ স্থায়ী ঠিকানা:', 231.7, 85.3),
    hline(234.4), hline(247.5), hline(260.5),
    vline(L, 234.4, 260.5), vline(R, 234.4, 260.5),
    {
        k: 'wrap',
        field: 'permanentAddress',
        lines: [{ row: [234.4, 247.5], from: L + VALUE_INSET, to: R - VALUE_INSET }, { row: [247.5, 260.5], from: L + VALUE_INSET, to: R - VALUE_INSET }],
    },

    // ৪. বর্তমান ঠিকানা
    head('৪। বর্তমান ঠিকানা:', 279.4, 85.4),
    hline(282.1), hline(295.6), hline(308.4),
    vline(L, 282.1, 308.4), vline(R, 282.1, 308.4),
    {
        k: 'wrap',
        field: 'presentAddress',
        lines: [{ row: [282.1, 295.6], from: L + VALUE_INSET, to: R - VALUE_INSET }, { row: [295.6, 308.4], from: L + VALUE_INSET, to: R - VALUE_INSET }],
    },

    // ৫. আবেদনকারীর/তথ্যপ্রদানকারীর প্রত্যয়ন
    head('৫. আবেদনকারীর/তথ্যপ্রদানকারীর প্রত্যয়ন  :', 327.5, 84.9),
    hline(330.4), hline(352.3), hline(367.7), hline(382.6),
    vline(L, 330.4, 382.6), vline(R, 330.4, 382.6), vline(390.1, 330.4, 382.6),
    vline(133.75, 352.3, 382.6), vline(272.6, 352.3, 367.7), vline(434.9, 352.3, 382.6), vline(484.0, 352.3, 382.6),
    center('আমি স্বজ্ঞানে ঘোষণা করিতেছি যে, উপরে বর্ণিত যাবতীয় তথ্য সঠিক,আমার/তাহার অন্য কোথাও জন্ম', 338.6, 237.5),
    center('নিবন্ধিত হয় নাই।', 349.2, 237.4),
    center('আবেদনের তারিখ (অংকে)', 338.4, mid(390.1, R)),
    center('নাম', 362.2, mid(L, 133.75)),
    inCell('declarantName', [352.3, 367.7], 133.75 + VALUE_INSET, 272.6 - VALUE_INSET),
    text('স্বাক্ষর/ টিপসহি', 360.0, 343.6),
    cellValue('applicationDay', [352.3, 367.7], 390.1, 434.9),
    cellValue('applicationMonth', [352.3, 367.7], 434.9, 484.0),
    cellValue('applicationYear', [352.3, 367.7], 484.0, R),
    text('সম্পর্ক', 377.4, 90.4),
    tick('declarantRelation', 'father', 139.0, 372.6, 4.8, 5.1),
    text('পিতা', 379.3, 147.3),
    tick('declarantRelation', 'mother', 168.5, 372.6, 4.6, 5.0),
    text('মাতা (টিক চিহ্ন দিন)। অন্যান্য (সম্পর্ক লিখুন):', 379.3, 175.5),
    leader(379.3, 304.5, 380.3),
    value('declarantRelationOther', 379.3, 304.5, 388.5, { lift: VALUE_LIFT, inset: 1 }),
    center('দিন', 377.5, mid(390.1, 434.9)),
    center('মাস', 377.5, mid(434.9, 484.0)),
    center('বৎসর', 377.5, mid(484.0, R)),

    // ৭. নিবন্ধকের কার্যালয় কর্তৃক পূরণীয় — office use, printed blank.
    // (The official form numbers this block ৭; there is no ৬.)
    head('৭. নিবন্ধকের কার্যালয় কর্তৃক পূরণীয় :', 390.8, 85.3),
    hline(393.9), hline(457.7), hline(479.4),
    vline(L, 393.9, 479.4), vline(199.1, 393.9, 479.4), vline(337.1, 393.9, 457.7), vline(R, 393.9, 479.4),
    center('নিবন্ধকের অনুমোদন (স্বাক্ষর, তারিখ', 401.9, mid(L, 199.1)),
    center('ও নামসহ সীল)', 412.3, mid(L, 199.1)),
    text('নিবন্ধন বহি নং:', 401.8, 204.5),
    text('নিবন্ধনের তারিখ:', 412.3, 204.5),
    leader(423.0, 204.5, 232.5), text('/', 423.0, 233.3), leader(423.0, 236.5, 266.8), text('/', 423.0, 267.5), leader(423.0, 270.8, 298.5),
    // The visarga is attached to the word: set alone after a space, Kalpurush
    // draws it on a dotted-circle placeholder.
    text('নিবন্ধন নংঃ', 433.3, 204.5),
    leader(433.3, 238.0, 293.0),
    leader(444.3, 204.5, 237.3), text('/', 444.3, 238.3), leader(444.3, 243.5, 278.3), text('/', 444.3, 279.8), leader(444.3, 284.5, 330.3),
    text('দিন', 454.4, 212.0),
    text('মাস', 454.4, 260.7),
    text('বৎসর', 454.4, 309.9),
    center('নিবন্ধন বহিতে লিপিবদ্ধকারীর স্বাক্ষর, তারিখ ও নামসহ সীল', 412.1, mid(337.1, R)),
    center('জন্ম সনদ প্রদানের সম্ভাব্য তারিখ', 465.5, 142.0),
    leader(465.8, 204.8, 237.3), text('/', 465.8, 238.3), leader(465.8, 243.5, 278.3), text('/', 465.8, 279.8), leader(465.8, 284.8, 330.3),
    text('দিন', 476.3, 204.5),
    text('মাস', 476.3, 253.2),
    text('বৎসর', 476.3, 302.9),

    // Cut here — the slip below goes back to the applicant.
    { k: 'scissors', x: 86.5, y: 491.6, w: 9, h: 7.2 },
    { k: 'dash', y: 495.9, x0: 96.0, x1: 526.3 },
    head('আবেদনকারীর অংশঃ (নিবন্ধকের কার্যালয় নিচের অংশটি পূরণ করিয়া আবেদনকারীকে ফেরত দিবেন)', 508.8, 85.5),
    hline(522.5), hline(533.6), hline(555.4), hline(577.3),
    vline(L, 522.5, 577.3), vline(182.75, 522.5, 555.4), vline(332.75, 533.6, 577.3),
    vline(438.75, 533.6, 555.4), vline(R, 522.5, 577.3),
    text('নিবন্ধনাধীন ব্যক্তির নাম', 530.6, 91.3),
    inCell('registrantName', [522.5, 533.6], 182.75 + VALUE_INSET, R - VALUE_INSET),
    text('বর্তমান ঠিকানা', 541.6, 90.7),
    {
        k: 'wrap',
        field: 'presentAddress',
        size: 8,
        minSize: 6,
        // Two lines share one 21.8 pt cell here, so each keeps a fixed baseline.
        lines: [{ y: 542.6, from: 182.75 + VALUE_INSET, to: 332.75 - VALUE_INSET }, { y: 552.4, from: 182.75 + VALUE_INSET, to: 332.75 - VALUE_INSET }],
    },
    text('জন্ম সনদ প্রদানের সম্ভাব্য তারিখ', 541.3, 338.2),
    leader(541.5, 444.3, 463.5), text('/', 541.5, 464.3), leader(541.5, 469.8, 490.8), text('/', 541.5, 491.6), leader(541.5, 497.3, 516.3),
    text('দিন', 552.0, 444.2),
    text('মাস', 552.0, 473.5),
    text('বৎসর', 552.0, 501.2),
    text('নিবন্ধকের কার্যালয়ের সংশ্লিষ্ট কর্মকর্তার তারিখসহ স্বাক্ষর ও সীল', 568.6, 338.3),

    // Footnote
    hline(660.0, 85.3, 527.5, { stroke: 0.9 }),
    text('১', 666.0, 85.5, { size: TYPE.sup.size }),
    text('এই ফরমটি বিনা মূল্যে বিতরণের জন্য: আবেদনকারী বা নিবন্ধক উহা কপি করিয়া ব্যবহার করিতে পারিবে।', 670.1, 89.1),
];
